import { acceptHMRUpdate, defineStore } from 'pinia'
import { getFields, getFieldChangeInfos } from '@/scripts/task'
import { useCustomers } from './customers'
import { useMaps } from './maps'
import { colors } from '@/scripts/util/colors'
import { getFieldPathStatistics } from '@/scripts/task'
import { useTasksStore } from './tasks'
import { useUsersManagementStore } from './usersManagement'
import i18n from '@/i18n'
import { removeUnderline } from '@/helpers/stringHelper'
import { area } from '@turf/area'

export const useFieldsManagementStore = defineStore('fieldsManagement', {
    state: () => ({
        fields: [],
        originalFields: [],
        searchQuery: '',
        loading: false,
        taskDialogVisible: false,
        selectedTask: null,
        selectedTaskType: null,
        selectedPath: null,
        inputDate: null,
        otherWords: [
            i18n.global.t('No tasks'),
            i18n.global.t('No plans'),
            'N/A'
        ],
        utc: false
    }),
    actions: {
        async getFields() {
            this.loading = true
            const cStore = useCustomers()
            const ts = useTasksStore()
        
            const fields = await getFields({
                organization_ids: cStore.selectedCustomers.map((c) => c.id),
                expand_data: [
                    'outer_border', 
                    'ab_lines', 
                ]
            })
            this.fields = fields
        
            // Process all fields in parallel for better performance
            const fieldPromises = this.fields.map(async (field, index) => {
                field.color = colors[index % colors.length]
                field.table_svg_id = `table-svg-${field.id}`
                field.obstacles = this.getObstaclesCount(field).toString()
                field.commit_msg = await getFieldChangeInfos(field.id)
                field.area = field.outer_border ? area(field.outer_border) : 0

                // Get path statistics
                field.path_statistic = await getFieldPathStatistics(field.id)
                delete field.path_statistic.all
                delete field.path_statistic.disabled
        
                // Create task_statistic from non-zero values
                field.task_statistic = Object.keys(field.path_statistic)
                    .filter(key => field.path_statistic[key] !== 0)
                    .map(key => ({ [key]: field.path_statistic[key] }))
        
                // Get field tasks
                await ts.getFieldTasks(field.id)
                field.tasks = ts.tasks
                
                // Initialize plans object
                field.plans = {}
        
                // Get unique task types from task_statistic
                const taskTypes = field.task_statistic.map(stat => Object.keys(stat)[0])
        
                let noPathsCount = 0
                // Process plans from existing task.plans
                for (const task of Object.values(field.tasks)) {
                    if (task.plans && task.plans.length > 0) {
                        // Filter plans that match our task types
                        const relevantPlans = task.plans.filter(plan => taskTypes.includes(plan.type))
                        
                        // Group plans by their type
                        relevantPlans.forEach(plan => {
                            if (!field.plans[plan.type]) {
                                field.plans[plan.type] = []
                            }
                            field.plans[plan.type].push(plan)

                            // add the field.id to the plan
                            plan.field_id = field.id
                            // add the task.id to the plan
                            plan.task_id = task.id
                        })
                    } else {
                        // If task has no plans, count it
                        noPathsCount++
                    }
                }

                // Add count of tasks without plans to task_statistic
                if (noPathsCount > 0) {
                    field.task_statistic.push({
                        no_paths: noPathsCount
                    })
                }

                return field
            })
        
            // Wait for all fields to be processed
            this.fields = await Promise.all(fieldPromises)
            
            this.fields.forEach(field => {
                field.table_svg_id = `table-svg-${field.id}`
            })
            this.originalFields = [...this.fields]
            this.loading = false
        },
        async getDisabledFields() {
            const mapStore = useMaps()
            await mapStore.getAllDisabledFields()
            
            this.fields = [...this.fields, ...mapStore.disabledFields]
        },
        // convert date to YYYY-MM-DD format based on UTC setting
        getFormattedDate(date) {
            if (this.utc) {
                // UTC: toISOString format YYYY-MM-DD
                return date.toISOString().split('T')[0]
            } else {
                // Local timezone: convert to local timezone but keep YYYY-MM-DD format
                const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
                return localDate.toISOString().split('T')[0]
            }
        },
        // get task date based on UTC setting
        getTaskDate(task) {
            if (this.utc) {
                // UTC: direct data from DB (format YYYY-MM-DD)
                return task.last_working.slice(0, 10) // YYYY-MM-DD
            } else {
                // Local timezone: convert timestamp from DB to local timezone but keep YYYY-MM-DD format
                const taskDateObj = new Date(task.last_working)
                const localDate = new Date(taskDateObj.getTime() - (taskDateObj.getTimezoneOffset() * 60000))
                return localDate.toISOString().split('T')[0]
            }
        },
        // filter tasks for a field
        filterTasksForField(field, filterDate) {
            if (!field.tasks) return []

            return Object.values(field.tasks).filter(task => {
                if (!task.last_working) return false
        
                const taskDate = this.getTaskDate(task)
                return taskDate >= filterDate
            })
        },
        // calculate task statistics
        calculateTaskStatistics(validTasks) {
            const stats = {}
            let noPaths = 0

            validTasks.forEach(task => {
                if (task.plans?.length > 0) {
                    task.plans.forEach(plan => {
                        const type = plan.type || 'unknown'
                        stats[type] = (stats[type] || 0) + 1
                    })
                } else {
                    noPaths++
                }
            })

            return {
                statistics: [
                    ...Object.entries(stats).map(([type, count]) => ({ [type]: count })),
                    ...(noPaths > 0 ? [{ no_paths: noPaths }] : [])
                ],
                tasks: validTasks.reduce((acc, task) => {
                    acc[task.id] = task
                    return acc
                }, {})
            }
        },
        // update field with filtered data
        updateFieldWithFilteredData(field, validTasks) {
            const { statistics, tasks } = this.calculateTaskStatistics(validTasks)
    
            field.task_statistic = statistics
            field.tasks = tasks
    
            return field
        },
        // main filter tasks function
        getFilteredTasks(input) {
            this.inputDate = input
            this.resetInputDate()

            if (!this.inputDate) return

            // convert input date to format YYYY-MM-DD
            const filterDate = this.getFormattedDate(this.inputDate)

            // filter fields and recalculate statistics
            this.fields = this.fields.filter(field => {
                const validTasks = this.filterTasksForField(field, filterDate)
        
                if (validTasks.length === 0) return false

                this.updateFieldWithFilteredData(field, validTasks)
                return true
            })
        },
        resetInputDate() {
            const mapStore = useMaps()

            this.fields = [...this.originalFields, ...mapStore.disabledFields]
        },
        clearFilter() {
            const mapStore = useMaps()

            this.searchQuery = ''
            this.fields = [...this.originalFields, ...mapStore.disabledFields]
            this.inputDate = null
        },    
        filteredFields() {
            if (!this.searchQuery) {
                return this.fields
            }

            const q = this.searchQuery.toLowerCase()

            const ums = useUsersManagementStore()
            
            return this.fields.filter(field => {
                return (
                    field.id.toString().includes(q) ||
                    field.field_name.toLowerCase().includes(q) ||
                    field.farm_id.toString().includes(q) ||
                    ums.formatDate(field.created).toString().includes(q) ||
                    ums.formatDate(field.last_changed).toString().includes(q) ||
                    field.changed_by?.toLowerCase().includes(q) ||
                    field.track_width?.toString().includes(q) ||
                    field.config?.toString().includes(q) ||
                    field.user_comment?.toLowerCase().includes(q) ||
                    field.internal_comment?.toLowerCase().includes(q) ||
                    field.quality_comment?.toLowerCase().includes(q) ||
                    this.getObstaclesCount(field).toString().includes(q) ||
                    field.area?.toString().includes(q) ||
                    
                    (field.task_statistic && field.task_statistic.some(taskStat => 
                        i18n.global.t(removeUnderline(Object.keys(taskStat)[0])).toLowerCase().includes(q)
                    )) ||

                    (field.plans && Object.entries(field.plans).some(([planType, plans]) => 
                        planType.toLowerCase().includes(q) ||
                        plans.some(plan => 
                            (plan.name && plan.name.toLowerCase().includes(q)) ||
                            plan.id.toString().includes(q)
                        )
                    )) ||

                    ((!field.task_statistic || field.task_statistic.length === 0) && this.otherWords.some(word => 
                        word.toLowerCase().includes(q)
                    ))
                )
            })
        },
        getObstaclesCount(field) {
            if (!field.outer_border) {
                return 0
            }
            return field.outer_border.coordinates.length - 1
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useFieldsManagementStore, import.meta.hot))
}